import { useLayoutEffect, useState } from 'react'

// Where "Sign in another device" goes (owner 2026-10-19): in the header
// whenever the row has room for it, with its words when there is room for
// those too; otherwise in the account menu. It is the first thing to give
// way: Transfers, the bell and the account menu always stay, and the logo
// keeps its own scaling (header.scss) for when even those don't fit.
export const SIGN_IN_FULL = 'full'
export const SIGN_IN_ICON = 'icon'
export const SIGN_IN_MENU = 'menu'

// Sub-pixel rounding between the parts and the row.
const SLACK = 0.5

// The decision, from widths that never depend on it: the row; the brand at
// its natural size (not shrunk); the localhost page-size badge; the other
// buttons (with the row's own padding); and the button itself with its
// words and without, measured off-screen.
export const signInDeviceFit = ({ row, brand = 0, pageSize = 0, actionsBase = 0, full = 0, icon = 0 }) => {
  const used = brand + pageSize + actionsBase
  if (used + full <= row + SLACK) return SIGN_IN_FULL
  if (used + icon <= row + SLACK) return SIGN_IN_ICON
  return SIGN_IN_MENU
}

const isRendered = el => !!el && el.getClientRects().length > 0

const outerWidth = el => {
  if (!isRendered(el)) return 0
  const style = window.getComputedStyle(el)
  return el.getBoundingClientRect().width + (parseFloat(style.marginLeft) || 0) + (parseFloat(style.marginRight) || 0)
}

// How much narrower the logo is drawn than its natural size: the brand
// gives way before anything else (header.scss), and that room is the
// brand's, not free.
const logoShortfall = brand => {
  const img = brand && Array.from(brand.querySelectorAll('img')).find(isRendered)
  if (!img || !img.naturalWidth || !img.naturalHeight) return 0
  const box = img.getBoundingClientRect()
  return Math.max(0, (box.height * img.naturalWidth) / img.naturalHeight - box.width)
}

export const readHeaderRoom = ({ row, brand, pageSize, actions, signIn, measureFull, measureIcon }) => ({
  row: row.current ? row.current.getBoundingClientRect().width : 0,
  brand: outerWidth(brand.current) + (isRendered(brand.current) ? logoShortfall(brand.current) : 0),
  pageSize: pageSize.current ? pageSize.current.getBoundingClientRect().width : 0,
  actionsBase: (actions.current ? actions.current.getBoundingClientRect().width : 0) - outerWidth(signIn.current),
  full: outerWidth(measureFull.current),
  icon: outerWidth(measureIcon.current)
})

// Measures before the first paint (no flash of the wrong layout), then again
// on any change of the row, the brand (the logo loading) or the buttons
// (Transfers coming and going, a badge), at most once a frame. The inputs
// don't depend on the result, so a change it causes measures the same and
// settles: no loop.
export const useSignInDeviceFit = refs => {
  const [fit, setFit] = useState(SIGN_IN_FULL)

  useLayoutEffect(
    () => {
      let frame = 0

      const measure = () => {
        frame = 0
        const next = signInDeviceFit(readHeaderRoom(refs))
        setFit(prev => (prev === next ? prev : next))
      }

      const schedule = () => {
        if (!frame) frame = window.requestAnimationFrame(measure)
      }

      measure()

      let observer = null
      if (typeof window.ResizeObserver === 'function') {
        observer = new window.ResizeObserver(schedule)
        ;[refs.row.current, refs.brand.current, refs.actions.current].forEach(el => el && observer.observe(el))
      }
      window.addEventListener('resize', schedule)
      const brand = refs.brand.current
      // the logo arriving (load doesn't bubble; capture sees it)
      if (brand) brand.addEventListener('load', schedule, true)

      return () => {
        if (frame) window.cancelAnimationFrame(frame)
        if (observer) observer.disconnect()
        window.removeEventListener('resize', schedule)
        if (brand) brand.removeEventListener('load', schedule, true)
      }
    },
    // the refs are stable; measuring follows the observers, not renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return fit
}
